import { FC, useMemo } from 'react';
import { useFragment } from 'react-relay';
import { AxisOptions, Chart } from 'react-charts';
import { useTheme } from '@mui/material/styles';
import graphql from 'babel-plugin-relay/macro';

import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';

import { SeoRouteEditStats_seoRoute$key } from '__generated__/SeoRouteEditStats_seoRoute.graphql';

type Props = {
  seoRoute: SeoRouteEditStats_seoRoute$key;
};

type Counts = {
  date: string;
  count: number;
};

type Series = {
  label: string;
  data: Counts[];
};

const SeoRouteEditStats: FC<Props> = props => {
  const { palette } = useTheme();

  const { stats } = useFragment(
    graphql`
      fragment SeoRouteEditStats_seoRoute on SeoRoute {
        stats {
          week
          sourceViews
          destinationViews
        }
      }
    `,
    props.seoRoute
  );

  const data: Series[] = [
    {
      label: 'Ateinančio URL aplankymai',
      data:
        stats.map(({ week, sourceViews }) => ({
          date: week,
          count: sourceViews,
        })) ?? [],
    },
    {
      label: 'Redirect URL aplankymai',
      data:
        stats.map(({ week, destinationViews }) => ({
          date: week,
          count: destinationViews,
        })) ?? [],
    },
    {
      label: 'Viso',
      data:
        stats.map(({ week, sourceViews, destinationViews }) => ({
          date: week,
          count: sourceViews + destinationViews,
        })) ?? [],
    },
  ];

  const primaryAxis = useMemo(
    (): AxisOptions<Counts> => ({
      getValue: datum => datum.date,
    }),
    []
  );

  const secondaryAxes = useMemo(
    (): AxisOptions<Counts>[] => [
      {
        getValue: datum => datum.count,
        elementType: 'line',
      },
    ],
    []
  );

  if (stats.length < 1)
    return (
      <Alert severity="info" variant="filled">
        Dar nėra statistikos
      </Alert>
    );

  return (
    <Paper sx={{ p: 2, height: '420px' }}>
      <Chart
        options={{
          data,
          primaryAxis,
          secondaryAxes,
          padding: { bottom: 30, right: 30 },
          dark: palette.mode === 'dark',
        }}
      />
    </Paper>
  );
};

export default SeoRouteEditStats;
